import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayView, Polygon } from '@react-google-maps/api';
import { Modal } from 'components-antd';
import classNames from 'classnames';

import { getFeedIsMapDrawingModeSelector } from 'store/selectors/feed';
import { MapDrawControlsFeedWrapper } from 'pages/Properties/Feed/components/MapDrawControlsFeedWrapper';

import { Locations, Map } from 'components';
import Button from 'components/Button';
import styles from './styles.module.scss';
import { getZoomForBounds } from './helper';
import { LocationType } from 'types';
import { toggleFeedMapDrawingModeAction } from 'store/actions/feed';
import { cloneDeep } from 'lodash-es';
import {
  getAgentPropertiesMarkersDrawSelector,
  getFeedCriteriaSelectorV3,
  getSavedSearchesPropertiesMarkersDrawSelector,
} from 'store/selectors/feedv3';
import { useAgentSearchInstance } from 'pages/Properties/AgentFeed/hooks/useAgentSearchInstance';
import { useSearchInstanceV3Effect } from 'pages/Properties/ClientFeed/hooks/useSearchInstanceV3Effect';
import { getUserRolesMapSelector } from 'store/selectors/user';
import { resetPropertiesDrawAction } from 'store/actions/feedv3';
import { Close } from 'components/Icons';

const CustomLocationModal = (props) => {
  const dispatch = useDispatch();
  const {
    setIsModalOpen,
    isModalOpen,
    onResult,
    multipleDraw,
    polygonLocations,
    setPolygonLocations,
    showDrawMarkers,
  } = props;
  const { criteria } = useSelector(getFeedCriteriaSelectorV3);
  const [initialValue] = useState(cloneDeep(polygonLocations));
  const [polygon, setPolygon] = useState<google.maps.Polygon | null>(null);
  const [location, setLocation] = useState<any>(null);
  const isDrawingMode = useSelector(getFeedIsMapDrawingModeSelector);
  const [worldDim, setWorldDim] = useState({ height: 256, width: 256 });
  const [center, setCenter] = useState({ lat: 39.8283, lng: -98.5795 });
  const [mapZoom, setMapZoom] = useState(12);
  const [showDrawHint, setShowDrawHint] = useState<boolean>(true);
  const { isAgent } = useSelector(getUserRolesMapSelector);
  const { agentPropertiesMarkers, isPending: isPendingAgent } = useSelector(
    getAgentPropertiesMarkersDrawSelector,
  );
  const getAgentSearchInstance = useAgentSearchInstance();
  const { propertiesMarkers, isPending: isPendingClient } = useSelector(
    getSavedSearchesPropertiesMarkersDrawSelector,
  );
  const getSearchInstanceV3 = useSearchInstanceV3Effect();

  const mapRef = useRef<google.maps.Map | null>(null);
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const isDrawingRef = useRef(false);
  const [hoverPolygonIndex, setHoverPolygonIndex] = useState<any>(null);

  const handleMouseMove = useCallback((e: google.maps.MapMouseEvent) => {
    if (!isDrawingRef.current || !e.latLng || !polylineRef.current) return;
    polylineRef.current.getPath().push(e.latLng);
  }, []);

  useEffect(() => {
    if (criteria?.Locations) {
      const { lat, lng } = criteria?.Locations?.[0]?.Center || {};
      if (lat && lng) {
        setCenter({
          lat,
          lng,
        });
        setMapZoom(12);
      }
    }
  }, []);

  const onShapeDraw = async (polyline: google.maps.Polyline) => {
    // Convert Polyline to Polygon for compatibility
    const path = polyline.getPath().getArray();

    if (path.length < 3) {
      polyline.setMap(null);
      return;
    }

    // Close the shape
    path.push(path[0]);

    const polygon = new google.maps.Polygon({
      paths: path,
      map: polyline.getMap(),
    });

    setPolygon(polygon);
    const geojsonPolygon: any = await gmapsPolygonToGeoJson(polygon);
    if (geojsonPolygon?.coordinates?.[0]?.length < 4) {
      polygon.setMap(null);
      return;
    }

    const newPolygon = {
      Type: LocationType.Polygon,
      Polygon: geojsonPolygon,
    };

    setPolygonLocations((prev) => {
      const locations = [...prev, newPolygon];
      dispatch(toggleFeedMapDrawingModeAction(false));
      setCenterForCustomPolygon();
      fetchProperties(locations);

      return locations;
    });

    polyline.setMap(null);
  };

  const stopDrawing = useCallback(() => {
    if (!isDrawingRef.current) return;

    isDrawingRef.current = false;
    if (polylineRef.current) {
      const path = polylineRef.current.getPath();
      if (path.getLength() > 2) {
        path.push(path.getAt(0)); // close shape
      }
      onShapeDraw?.(polylineRef.current);
    }

    window.removeEventListener('mouseup', stopDrawing);
    dispatch(toggleFeedMapDrawingModeAction(false));
  }, [onShapeDraw]);

  // Dynamically update gestureHandling
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setOptions({
        gestureHandling: isDrawingMode ? 'none' : 'auto',
      });
    }
  }, [isDrawingMode]);

  const handleLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;

      map.addListener('mousedown', (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;

        isDrawingRef.current = true;

        const polyline = new google.maps.Polyline({
          strokeColor: '#000000',
          strokeOpacity: 1,
          strokeWeight: 2.5,
          map,
          path: [e.latLng],
        });

        polyline.setMap(map);
        polylineRef.current = polyline;
        map.addListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', stopDrawing);
      });
    },
    [handleMouseMove, stopDrawing, isDrawingMode],
  );

  // Clean up on unmount
  useEffect(() => {
    return () => {
      window.removeEventListener('mouseup', stopDrawing);
    };
  }, [stopDrawing]);

  const handleSave = () => {
    onResult(polygonLocations);
    setIsModalOpen(false);
    dispatch(resetPropertiesDrawAction());
  };

  const handleClose = () => {
    setPolygonLocations(initialValue);
    setIsModalOpen(false);
    dispatch(resetPropertiesDrawAction());
  };

  const polygonOptions: google.maps.PolygonOptions = {
    strokeColor: '#252D44',
    fillColor: '#252D44',
    fillOpacity: 0.08,
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCenter({ lat: latitude, lng: longitude });
          setMapZoom(12);
        },
        (error) => {
          console.error('Error retrieving location:', error);
        },
      );
    } else {
      console.error('Geolocation is not supported by this browser.');
    }
  };

  useEffect(() => {
    if (polygonLocations && polygonLocations.length > 0) setCenterForCustomPolygon();
    else getCurrentLocation();

    navigator.permissions.query({ name: 'geolocation' }).then((permissionStatus) => {
      // Set up a listener to detect permission state changes
      permissionStatus.onchange = () => {
        if (permissionStatus.state === 'granted') {
          getCurrentLocation();
        }
      };
    });
  }, []);

  useEffect(() => {
    if (polygon) {
      polygon.setMap(null);
    }
    if (polygonLocations?.length && !isDrawingMode) {
      setPolygon(geoJsonPolygonToGmaps(polygonLocations?.[0]?.Polygon));
    }
  }, [polygonLocations]);

  useEffect(() => {
    if (isDrawingMode) {
      if (polygon) {
        polygon.setMap(null);
      }
      setPolygon(null);
    }
  }, [isDrawingMode]);

  const setCenterForCustomPolygon = () => {
    const mapContainer = document.querySelector(`.${styles.mapWrapper}`) as HTMLElement;
    const bounds = new window.google.maps.LatLngBounds();
    let latSum = 0;
    let lngSum = 0;
    let totalPoints = 0;

    if (mapContainer) {
      setWorldDim({
        height: mapContainer.offsetHeight,
        width: mapContainer.offsetWidth,
      });
    }

    polygonLocations.forEach(({ Polygon: polygon }) => {
      const { coordinates } = polygon;
      if (coordinates !== null) {
        coordinates[0].forEach((coord) => {
          lngSum += coord[0];
          latSum += coord[1];
          totalPoints++;
          bounds.extend(new window.google.maps.LatLng(coord[1], coord[0]));
        });
      }
    });

    if (totalPoints > 0) {
      const center = {
        lat: latSum / totalPoints,
        lng: lngSum / totalPoints,
      };
      const zoom = getZoomForBounds(bounds, worldDim);
      setMapZoom(zoom);
      setCenter(center);
    }
  };

  function geoJsonPolygonToGmaps(geoJsonPolygon: any): google.maps.Polygon {
    const gmapsPolygon = new google.maps.Polygon();
    const coordinates = geoJsonPolygon.coordinates[0]
      .slice(0, -1) // remove the last point which is the same as the first in geojson
      .map(([lng, lat]) => new google.maps.LatLng(lat, lng));
    gmapsPolygon.setPaths(coordinates);
    return gmapsPolygon;
  }

  const fetchProperties = (Locations) => {
    if (Locations?.length && showDrawMarkers) {
      const body = {
        criterias: { ...criteria, Locations },
      };
      isAgent
        ? getAgentSearchInstance(body, undefined, undefined, undefined, undefined, undefined, true)
        : getSearchInstanceV3(body, undefined, undefined, true);
    }
  };

  async function gmapsPolygonToGeoJson(polygon: google.maps.Polygon): Promise<object> {
    var dataLayer = new window.google.maps.Data();
    dataLayer.add(
      new window.google.maps.Data.Feature({
        geometry: new window.google.maps.Data.Polygon([polygon.getPath().getArray()]),
      }),
    );

    return new Promise((resolve) => {
      dataLayer.toGeoJson((geoJsonValue) => {
        const firstFeature = (geoJsonValue as any).features[0];
        const geojsonPolygon = {
          type: 'Polygon',
          coordinates: firstFeature.geometry.coordinates,
        };
        resolve(geojsonPolygon);
      });
    });
  }

  const clearDrawing = () => {
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current.setPath([]);
      polylineRef.current = null;
    }
    polygon && polygon.setMap(null);
    isDrawingRef.current = false;
  };

  const handleRemoveBoundary = () => {
    if (hoverPolygonIndex === null) return;
    const newLocations = [...polygonLocations];
    newLocations.splice(hoverPolygonIndex, 1);
    setPolygonLocations(newLocations);
    setHoverPolygonIndex(null);
    fetchProperties(newLocations);
  };

  const loading = isPendingAgent || isPendingClient;

  return (
    <Modal
      open={isModalOpen}
      footer={null}
      onCancel={handleClose}
      className={styles.inviteModal}
      cancelButtonStyles={styles.crossButton}
      maskClosable={false}
      destroyOnClose={true}
    >
      <div className={styles.inviteHeader}>
        <h2 className={styles.inviteHeading}>Draw Locations</h2>
      </div>
      <div className={styles.card}>
        <Locations
          shouldFilterStateLevelResults={true}
          name="locations"
          placeholder="Enter city, ZIP, or neighborhood"
          placeholderClassName={styles.border}
          searchWrapperClassName={styles.border}
          valuesWrapperClassName={styles.border}
          onResult={(_, preparedData) => {
            setPolygonLocations([]);
            setLocation(preparedData);
            setCenter(preparedData?.[0]?.Center);
            setMapZoom(12);
          }}
          testid="desired_locations"
          multiple={false}
          variant={Locations.ROUND}
          value={location || []}
          rounded={true}
          customLoading={loading}
        />
      </div>
      <div>
        <div className={styles.mapWrapper}>
          <span
            onClick={() => {
              setShowDrawHint(false);
            }}
          >
            <Map
              popupClassName={styles.popupClassName}
              center={center}
              mapContainerClassName={styles.map}
              zoom={mapZoom}
              controls={{ center: true }}
              noFitBounds={!showDrawMarkers}
              markersMap={
                polygonLocations?.length && showDrawMarkers && !loading && !isDrawingMode
                  ? isAgent
                    ? agentPropertiesMarkers
                    : propertiesMarkers
                  : []
              }
              isDrawModal
              callBackOnLoad={handleLoad}
            >
              {polygonLocations?.length &&
                polygonLocations?.map(({ Polygon: poly }, index) => {
                  const path = geoJsonPolygonToGmaps(poly).getPath();
                  const firstLatLng = path.getLength() > 0 ? path.getAt(0) : null;
                  return (
                    <>
                      <Polygon
                        key={index}
                        path={path}
                        onMouseOver={() => setHoverPolygonIndex(index)}
                        onMouseOut={() => setHoverPolygonIndex(null)}
                        options={{
                          ...polygonOptions,
                          fillOpacity: hoverPolygonIndex === index ? 0.5 : 0.2, // Optional highlight effect
                        }}
                      />
                      {hoverPolygonIndex === index && firstLatLng && (
                        <OverlayView
                          position={firstLatLng}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <div
                            className={styles.removeBoundaryBtn}
                            onClick={handleRemoveBoundary} // handler for removing the polygon
                          >
                            <Close color="#fff" className={styles.closeIcon} />
                            <span>Remove Boundary</span>
                          </div>
                        </OverlayView>
                      )}
                    </>
                  );
                })}
            </Map>
          </span>
          <div className={styles.mapControls}>
            <div className={styles.mapControlContainer}>
              {!loading && (
                <MapDrawControlsFeedWrapper
                  multipleDraw={multipleDraw}
                  setPolygonLocations={setPolygonLocations}
                  polygonLocations={polygonLocations}
                  showDrawHint={showDrawHint}
                  setShowDrawHint={setShowDrawHint}
                  clearMap={clearDrawing}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.buttonWrapper}>
        <Button
          onClick={() => handleSave()}
          className={classNames(styles.button, styles.deleteButton)}
          title="Save"
        />
      </div>
    </Modal>
  );
};

export default CustomLocationModal;
